import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FiInfo } from "react-icons/fi"

import { Title, Container, Number, Checkbox, Select } from "components/Form"
import { limsSstTemplates } from "state_management"
import {
  SST_INSTRUMENT_TYPES,
  instrumentTypeLabelKey,
  selectByInstrumentType,
  selectTemplate,
} from "state_management/lims/limsSstTemplates"
import useT from "utils/hooks/useT"

// SystemSuitability — edits the System Suitability Test specification of an Analysis definition
// (PRD-005 Area 3 / SPEC-017): whether the analysis REQUIRES an SST before results can be sent to
// review, and the SST acceptance criteria (RSD-area ≤, RSD-RT ≤, tailing interval, plate count N ≥,
// resolution Rs ≥, and the expected injection count). Shown only when the LIMS module is on,
// mirroring the Review-Specification section.
//
// It is a controlled component: the parent (AnalysisConfigurationPage) owns the `spec` object and
// persists it via the limsSstSpec slice on save (keyed by the analysis-definition id). An unset
// (blank) criterion simply is not judged for that parameter.
//
// RR8 #4065: when a DEFAULT SST TEMPLATE is chosen, the criteria come FROM that template and are
// shown read-only. Previously they stayed editable and blank even with a template selected, so the
// screen implied the analysis had no criteria while the run actually used the template's — and any
// value typed here was silently shadowed at run time. A template is change-controlled master data
// (reviewer + supervisor approved, versioned); editing its criteria per analysis would bypass that,
// so it is view-only here and maintained on /lims/sst-templates.
//
// R23/R24/R25 (#4075) finish that thought: SST criteria come from a template, full stop. The
// per-analysis criteria are no longer editable at all, and the "Inline criteria (no template)"
// option is gone from both this picker and the run-time one in SstPanel — an SST written by hand on
// one analysis is unversioned, unapproved master data, which is the thing change control exists to
// prevent. Requiring an SST therefore requires an instrument type AND a template (R23, enforced in
// the parent's save guard).
//
// R25 grandfathers what already exists: a definition holding inline criteria and no template keeps
// JUDGING exactly as before and is shown read-only with a notice, and the R23 guard is keyed on the
// SST slice having actually CHANGED — so renaming such a definition does not force its owner to
// pick a template first.
//
// Layout (PRD-006 K20): the section heading lives INSIDE the box; the "requires SST" checkbox sits
// side by side with its label at a larger size; the grey criterion labels share ONE fixed column
// width so every row aligns; the explanation comes last via the shared info-icon convention. The
// shared `.LimsConfigBox-*` classes (defined in index.scss) are reused from ReviewSpecification —
// the single SCSS convention for the LIMS analysis-definition config boxes.
const LABEL_WIDTH = "16rem"

// The six ACCEPTANCE criteria, in render order. `injectionCount` is deliberately NOT one of them:
// it is how many injections the analyst is asked to enter, not a limit anything is judged against.
// Used to detect a grandfathered inline SST (R25).
const CRITERION_FIELDS = ["rsdAreaMax", "rsdRtMax", "tailingMin", "tailingMax", "plateCountMin", "resolutionMin"]

export default function SystemSuitability({ spec, onChange }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const set = (field, value) => onChange({ ...spec, [field]: value })

  // F4 (SPEC-036) — the live shared SST templates for the chosen instrument type, offered as the
  // optional DEFAULT template the run pre-selects. Load the library once so the picker can populate.
  const instrumentType = spec.instrumentType || ""
  const templates = useSelector((state) => selectByInstrumentType(state, instrumentType))
  // RR8 #4065: the chosen default template supplies the criteria shown below (read-only).
  const pickedTemplate = useSelector((state) => selectTemplate(state, spec.defaultSstTemplateId))
  const fromTemplate = !!(spec.defaultSstTemplateId && pickedTemplate)
  // R25: a definition still carrying hand-written criteria and no template. It keeps judging, is
  // shown read-only, and is never forced to migrate.
  const legacyInline =
    !fromTemplate &&
    CRITERION_FIELDS.some((f) => spec[f] !== null && spec[f] !== undefined && spec[f] !== "")
  // R24: one row per criterion, ALWAYS read-only — the template's value when one is chosen, the
  // grandfathered inline value otherwise. A local helper so the six rows cannot drift apart.
  const criterion = (labelKey, field) => {
    const value = fromTemplate ? pickedTemplate[field] : spec[field]
    return (
      <Container>
        <Title width={LABEL_WIDTH}>{t(labelKey)}</Title>
        <span className="LimsConfigBox-readonly-value">{value == null || value === "" ? "—" : value}</span>
      </Container>
    )
  }
  useEffect(() => {
    dispatch(limsSstTemplates.fetch())
  }, [dispatch])

  // Changing the instrument type clears the default template (it belonged to the previous type);
  // re-selecting the same type is a no-op that keeps it. Both fields update in one call so the
  // controlled spec never holds a default template that doesn't match its instrument type.
  const setInstrumentType = (value) => {
    const next = value || null
    const keepDefault = next === instrumentType ? spec.defaultSstTemplateId : null
    onChange({ ...spec, instrumentType: next, defaultSstTemplateId: keepDefault })
  }

  return (
    <>
      <div className="LimsConfigBox-fields">
        <label className="LimsConfigBox-checkbox">
          <Checkbox checked={!!spec.requiresSst} onChange={(v) => set("requiresSst", v)} />
          <span className="LimsConfigBox-checkbox-label">{t("limsSstRequires")}</span>
        </label>

        {/* F4 — instrument type drives the run-time SST template picker; the default template
            (filtered to that type) pre-selects one for the analyst. Both optional: with neither
            set, the inline criteria below apply (back-compatible). */}
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstInstrumentType")}</Title>
          <Select
            isMulti={false}
            includeNone
            noneLabel={t("limsSstInstrumentNone")}
            collection={SST_INSTRUMENT_TYPES.map((type) => ({ id: type, name: t(instrumentTypeLabelKey(type)) }))}
            value={instrumentType}
            onChange={(v) => setInstrumentType(v && v[0])}
            menuPlacement="bottom"
          />
        </Container>
        {instrumentType && templates.length > 0 && (
          <Container>
            <Title width={LABEL_WIDTH}>{t("limsSstDefaultTemplate")}</Title>
            {/* R24: no "Inline criteria (no template)" escape hatch — the criteria must come from
                change-controlled master data. */}
            <Select
              isMulti={false}
              collection={templates.map((tpl) => ({ id: tpl.id, name: `${tpl.name} (v${tpl.versionNumber})` }))}
              value={spec.defaultSstTemplateId || ""}
              onChange={(v) => set("defaultSstTemplateId", (v && v[0]) || null)}
              menuPlacement="bottom"
            />
          </Container>
        )}

        {/* R23: requiring an SST with no template is a definition that can never pass its own
            gate. Said here, at the field, as well as blocked at save. */}
        {spec.requiresSst && !spec.defaultSstTemplateId && (
          <p className="LimsConfigBox-help LimsConfigBox-help--inline">
            <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstTemplateRequired")} />
            <span>{t("limsSstTemplateRequired")}</span>
          </p>
        )}
        {fromTemplate && (
          <p className="LimsConfigBox-help LimsConfigBox-help--inline">
            <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstCriteriaFromTemplate")} />
            <span>{t("limsSstCriteriaFromTemplate")}</span>
          </p>
        )}
        {/* R25: the grandfathering notice — these criteria still judge, and nothing here forces a
            migration to a template. */}
        {legacyInline && (
          <p className="LimsConfigBox-help LimsConfigBox-help--inline">
            <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstInlineLegacy")} />
            <span>{t("limsSstInlineLegacy")}</span>
          </p>
        )}
        {criterion("limsSstRsdAreaMax", "rsdAreaMax")}
        {criterion("limsSstRsdRtMax", "rsdRtMax")}
        {criterion("limsSstTailingMin", "tailingMin")}
        {criterion("limsSstTailingMax", "tailingMax")}
        {criterion("limsSstPlateCountMin", "plateCountMin")}
        {criterion("limsSstResolutionMin", "resolutionMin")}
        {/* Still editable, deliberately: the injection count is not an acceptance criterion, and
            SstPanel reads it from THIS spec rather than from the template — so locking it to the
            template's value would display one number while the run used another, the exact
            divergence RR8 #4065 removed. Wiring the run time to the template's injectionCount is
            worth its own issue. */}
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstInjectionCount")}</Title>
          <Number min={1} value={spec.injectionCount} onChange={(v) => set("injectionCount", v)} />
        </Container>
      </div>

      <p className="LimsConfigBox-help">
        <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstRequiresHelp")} />
        <span>{t("limsSstRequiresHelp")}</span>
      </p>
    </>
  )
}
